import * as React from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import Box from "@mui/material/Box";
import {useEffect, useRef, useState} from "react";
import PreviewCSVTable from "./PreviewCSVTable";
import DataService from "../../DataService";
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import DangerousIcon from '@mui/icons-material/Dangerous';

function ListOfUrls({data}) {
    return (
        <>
            <Typography
                variant={'h6'}
            >
                Media Urls ({data.length} Lines)
            </Typography>
            <List dense={true}>
                {
                    data.map(
                        (row, index) => (
                            <ListItem
                                key={index}
                                sx={{
                                    borderBottom: "1px solid #f2f2f2",
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    marginRight: "1.5em",
                                                    background: "#f5f5f5",
                                                    width: "4em",
                                                }}
                                            >
                                                {index + 1}
                                            </Box>
                                            <Box>
                                                {row}
                                            </Box>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        )
                    )}
            </List>
        </>
    )
}


function DialogTextContent(
    {
        isLoading,
        isError,
        text,
    }
) {
    return (
        <Box
            sx={{
                margin: '3em auto',
                textAlign: 'center',
            }}
        >
            <Typography
                variant={'h3'}
            >
                Upload completed!
            </Typography>
        </Box>
    )
}


// Custom error classes for better error handling
class CSVValidationError extends Error {
    constructor(message, type = 'validation') {
        super(message);
        this.name = 'CSVValidationError';
        this.type = type;
    }
}

class CSVStructureError extends Error {
    constructor(message, type = 'structure') {
        super(message);
        this.name = 'CSVStructureError';
        this.type = type;
    }
}

function PreviewCSVModal(
    {
        open = false,
        handleClose,
        isLoading = false,
        csvData = [],
        detectMultipleValidation = [],
        detectionSettingId = null,
    }
) {

    const [csvType, setCsvType] = useState('list');
    const [error, setError] = useState(false);
    const [header, setHeader] = useState([]);
    const [rowCsv, setRowCsv] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [uploadCompleted, setUploadCompleted] = useState(false);
    const containerTable = useRef(null);
    const [errorText, setErrorText] = useState('');

    const isNaNValue = (string) => {
        const lowerStr = string.toLowerCase().trim();
        return lowerStr === 'n/a' || lowerStr === 'na';
    };

    const validateTimeFormat = (value) => {
        const timePattern = /^(\d{1,2}):(\d{2}):(\d{2})$/;
        const match = value.match(timePattern);
        
        if (!match) return false;
        
        const [, hours, minutes, seconds] = match.map(Number);
        return hours >= 0 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59;
    };

    const validateRangeValue = (value, options) => {
        if (!options || typeof options.min === 'undefined' || typeof options.max === 'undefined') {
            return false;
        }
        
        const numValue = parseInt(value, 10);
        return !isNaN(numValue) && numValue >= options.min && numValue <= options.max;
    };

    const validateField = (value, validation, options) => {
        if (isNaNValue(value)) return true;

        switch (validation) {
            case "hh:mm:ss":
                return validateTimeFormat(value);
            case 'range':
                return validateRangeValue(value, options);
            default:
                return true;
        }
    };

    const adjustValues = (value, validation) => {
        if (validation === "hh:mm:ss" && value.length === 7) {
            return `0${value}`;
        }
        return value;
    };

    // Helper functions for CSV processing
    const checkDataType = (data) => {
        const isList = data.filter(row => row.length === 1).length === data.length;
        const type = isList ? 'list' : 'table';
        setCsvType(type);
        return type;
    };

    const getMandatoryFields = () => {
        if (!detectMultipleValidation?.length) return [];
        return detectMultipleValidation
            .filter(el => {
                // Check if field has mandatoryForProjects array
                if (el.mandatoryForProjects && Array.isArray(el.mandatoryForProjects)) {
                    return el.mandatoryForProjects.includes(detectionSettingId);
                }
                // Fallback to legacy mandatory field
                return el.mandatory;
            })
            .map(el => el.name);
    };

    const createMediaUrlColumn = (colName) => ({
        field: 'mediaUrl',
        headerName: colName,
        width: 150,
        renderCell: (params) => {
            const { row } = params;
            const val = row['mediaUrl'];
            return (
                <a href={val} target="_blank" rel="noopener noreferrer">
                    {val}
                </a>
            );
        }
    });

    const createStandardColumn = (colName) => ({
        field: colName,
        headerName: colName,
        width: 150,
    });

    const processColumns = (cols) => {
        const mandatoryFields = getMandatoryFields();
        const remainingMandatoryFields = [...mandatoryFields];
        const generatedCols = [];
        const tableCols = [{
            field: 'id',
            headerName: 'N°',
            width: 80,
        }];
        let hasMediaUrl = false;

        cols.forEach(colName => {
            // Remove from mandatory fields if found
            const mandatoryIndex = remainingMandatoryFields.indexOf(colName);
            if (mandatoryIndex > -1) {
                remainingMandatoryFields.splice(mandatoryIndex, 1);
            }

            const cleanedColName = colName.replace(/[^A-Z0-9]/ig, "").toLowerCase();
            
            if (cleanedColName === 'mediaurl') {
                hasMediaUrl = true;
                tableCols.push(createMediaUrlColumn(colName));
                generatedCols.push('mediaUrl');
            } else {
                tableCols.push(createStandardColumn(colName));
                generatedCols.push(colName);
            }
        });

        return {
            tableCols,
            generatedCols,
            hasMediaUrl,
            remainingMandatoryFields
        };
    };

    const validateCellValue = (value, rule, rowIndex) => {
        if (!rule) return value;

        // Check if field is mandatory for current project
        let isMandatory = false;
        let isApplicable = true;
        if (rule.mandatoryForProjects && Array.isArray(rule.mandatoryForProjects)) {
            isMandatory = rule.mandatoryForProjects.includes(detectionSettingId);
            // If the field is scoped to specific projects and the current project
            // is not included, then the field must be blank (or omitted entirely)
            // regardless of the provided value.
            if (!isMandatory) {
                isApplicable = false;
            }
        } else {
            // Fallback to legacy mandatory field
            isMandatory = rule.mandatory;
        }

        // If the field is not applicable to this project, force it blank
        if (!isApplicable && value.length > 0) {
            throw new CSVValidationError(
                `A value was provided for "${rule?.name || 'field'}" on line ${rowIndex + 1}, but this field should be left empty or removed for this project.`,
                'presence_of_empty_mandatory_value'
            );
        }

        const rangeValidation = !!(rule.validationMax && rule.validationMin);
        const hasValidation = rule.validation || rangeValidation;

        if (hasValidation && (isMandatory || value.length > 0)) {
            const validationRule = rule.validation ?? 'range';
            const validationOptions = rangeValidation ? {
                min: rule.validationMin,
                max: rule.validationMax,
            } : null;

            if (!validateField(value, validationRule, validationOptions)) {
                throw new CSVValidationError(
                    `Cell validation failed for value "${value}" on line ${rowIndex + 1}. Expected format: ${validationRule}`,
                    'cell_validation'
                );
            }
            
            return adjustValues(value, rule.validation);
        }

        return value;
    };

    const processRowData = (data, generatedCols) => {
        // Create a lookup map for better performance
        const validationRules = new Map();
        if (detectMultipleValidation) {
            detectMultipleValidation.forEach(rule => {
                validationRules.set(rule.name, rule);
            });
        }

        return data.map((row, rowIndex) => {
            const cells = {};
            
            generatedCols.forEach((col, colIndex) => {
                let value = row[colIndex];
                
                // Normalize value
                if (value == null || value === '') {
                    value = '';
                } else {
                    value = String(value).trim();
                }
                
                // Apply validation if rule exists
                const rule = validationRules.get(col);
                if (rule) {
                    value = validateCellValue(value, rule, rowIndex);
                }
                
                cells[col] = value;
            });

            return {
                ...cells,
                id: rowIndex + 1,
            };
        });
    };

    const processTableData = () => {
        try {
            const cols = csvData[0].filter(el => el.length !== 0);
            const data = csvData.slice(1).filter(row => 
                row.filter(el => el.length !== 0).length !== 0
            );

            const { tableCols, generatedCols, hasMediaUrl, remainingMandatoryFields } = processColumns(cols);

            // Validate required fields
            if (remainingMandatoryFields.length > 0) {
                throw new CSVStructureError(
                    `Missing required columns: ${remainingMandatoryFields.join(', ')}`,
                    'missing_columns'
                );
            }

            if (!hasMediaUrl) {
                throw new CSVStructureError(
                    "Required column 'mediaUrl' is missing or misspelled",
                    'missing_media_url'
                );
            }

            const rows = processRowData(data, generatedCols);

            setHeader(tableCols);
            setRowCsv(rows);
            setError(false);
        } catch (e) {
            console.error('CSV processing error:', e);
            
            let errorMessage = e.message;
            if (e instanceof CSVValidationError || e instanceof CSVStructureError) {
                errorMessage = e.message;
            } else {
                errorMessage = 'An unexpected error occurred while processing the CSV file.';
            }
            
            setErrorText(errorMessage);
            setError(true);
        }
    };

    useEffect(() => {
        // Reset state
        setUploadCompleted(false);
        setIsSending(false);
        setError(false);
        setErrorText('');

        if (csvData.length > 0) {
            const dataType = checkDataType(csvData);
            if (dataType === 'table') {
                processTableData();
            }
        }
    }, [csvData, open])


    const submitCsv = () => {
        setIsSending(true)
        const dataToSend = [];
        if (csvType === 'list') {
            csvData.flat().forEach(el => {
                dataToSend.push({
                    mediaUrl: el,
                })
            })
        } else {
            rowCsv.forEach(el => {
                const rowCopy = el;
                delete rowCopy.id;
                const obj = {
                    mediaUrl: el.mediaUrl
                };
                delete rowCopy.mediaUrl;
                const meta = {};
                Object.keys(rowCopy).forEach(key => {
                    const rule = detectMultipleValidation.find(el => el.name === key);
                    const value = el[key];
                    // Skip fields that are not applicable to this project
                    if (rule && rule.mandatoryForProjects && Array.isArray(rule.mandatoryForProjects) &&
                        !rule.mandatoryForProjects.includes(detectionSettingId)) {
                        return;
                    }
                    if (String(value).length !== 0 || !!rule?.includeBlankValues) {
                        if (!!rule && !!rule['bodyParam']) {
                            obj[rule['bodyParam']] = el[key];
                            if (!!rule['includeInMeta']) {
                                meta[key] = el[key]
                            }
                        } else {
                            meta[key] = el[key]
                        }
                    }
                });
                obj['meta'] = meta;
                dataToSend.push(obj)
            })
        }


        console.log(dataToSend);

        DataService.postDetectMultipleJson(dataToSend)
            .catch(err => {
                console.error(err);
                setError(true);
            })
            .finally(() => {
                setUploadCompleted(true);
                setIsSending(false);
            })
        ;
    }


    return (
        <Dialog
            maxWidth={'xl'}
            fullWidth
            open={open}
            onClose={(event, reason) => {
                return;
            }}
            disableEscapeKeyDown={true}
        >
            <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <span>CSV Preview</span>
                <IconButton
                    onClick={_ => handleClose()}
                    sx={{ml: 'auto'}}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            {
                uploadCompleted &&
                <>
                    <DialogContent
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        {
                            error ?
                                <DangerousIcon
                                    color={'error'}
                                    sx={{
                                        mt: 4,
                                        fontSize: '4rem',
                                    }}
                                />
                                :
                                <CloudDoneIcon
                                    color={'success'}
                                    sx={{
                                        mt: 4,
                                        fontSize: '4rem',
                                    }}
                                />
                        }

                        <Typography
                            variant={'h4'}
                            color={error ? 'error.main' : 'success.main'}
                        >
                            Uploaded {error ? "Failed" : "Completed"}
                        </Typography>
                        <Typography
                            variant={'h6'}
                        >
                            {!error ? "The upload was successful. You can close this window Now"
                                : "The upload was unsuccessful. You can close this window and try again"}
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            borderTop: '1px solid #ccc',
                            py: 2,
                            px: 3,
                        }}

                    >
                        <Button
                            variant={'contained'}
                            color={'primary'}
                            autoFocus
                            onClick={_ => handleClose()}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </>
            }
            {
                !uploadCompleted &&
                <>
                    <DialogContent
                        ref={containerTable}
                    >
                        {
                            (isLoading || isSending)
                                ?
                                <Box
                                    sx={{
                                        margin: '2em auto',
                                        textAlign: 'center',
                                    }}
                                >
                                    <CircularProgress
                                        size={'4em'}
                                    />
                                    <Typography
                                        variant={'h3'}
                                    >
                                        {
                                            isSending
                                                ? "We are submitting the data..."
                                                : "We are loading the preview table..."
                                        }
                                    </Typography>
                                </Box>
                                :
                                <Box>


                                    {
                                        csvType === 'list' ?
                                            <ListOfUrls
                                                data={csvData.flat()}
                                            />
                                            : <PreviewCSVTable
                                                cols={header}
                                                data={rowCsv}
                                                error={error}
                                                errorText={errorText}
                                                parent={containerTable}
                                            />
                                    }

                                </Box>
                        }

                    </DialogContent>
                    <DialogActions
                        sx={{
                            borderTop: '1px solid #ccc',
                            py: 2,
                            px: 3,
                        }}

                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-end',
                            }}
                        >
                            <Box
                                sx={{
                                    color: 'error.main',
                                    textAlign: 'right'
                                }}
                            >
                                <Typography
                                    variant={'body1'}
                                >
                                    <strong>Attention</strong>: Please check the data above carefully before pressing
                                    ‘Submit’ as this action cannot be undone and media will be processed as submitted.
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    color: 'error.main',
                                    textAlign: 'right',
                                    ml: 1,
                                }}
                            >
                                <Button
                                    variant={'contained'}
                                    color={'primary'}
                                    disabled={isSending || error}
                                    onClick={_ => {
                                        submitCsv()
                                    }}
                                >
                                    Submit
                                </Button>
                            </Box>
                        </Box>
                    </DialogActions>

                </>
            }


        </Dialog>
    )
}

PreviewCSVModal.propTypes = {}

export default PreviewCSVModal
